/**
 * The round team mark of the Scores UX spec v1.0 (section 1.2): the logo
 * contained on the light `markLogoSurface` disc with a hairline ring, or,
 * with no logo, an initials plate in the team's `primaryColorHex` (else
 * `primarySolid`) with up to three letters in 700 weight and whichever of
 * white and the darkest neutral reads better on that fill.
 *
 * One component, three fixed sizes, so a score row, a schedule row and the
 * game header can never draw the same team two ways:
 *   row     28 (`scoreMarkSize`): score rows, schedule rows, head to head
 *   header  48 (`gameHeaderMarkSize`): the game detail scoreboard
 *   page    56: the team page header
 *   mini    20 (`iconMd`): the compact sticky bar
 *
 * The letters never touch the round edge. Three bold letters in `small` 12
 * can be wider than the circle is at their cap height ("WAS" and "WSH" are
 * about 28px of ink on the 28 row mark, whose chord there is about 26.6), so
 * the letters are measured once laid out and scaled down just enough to keep
 * them 2px clear of the edge, the inset a logo gets (`plateLettersScale`). Letters that fit are
 * drawn at full size.
 *
 * Never a pill, never badged, decorative (`aria-hidden`): the control around
 * it carries the team's name. The branch is on the URL alone (ISSUE-902 R5):
 * an image that fails falls back to the plate, so a fan never sees a broken
 * image.
 */
import { contrastRatio, palette, tokens } from "@sportsmet/theme";
import { cn } from "@sportsmet/ui";
import {
  type CSSProperties,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export type typeTeamDiscSize = "row" | "header" | "page" | "mini";

const SIZE_CLASS: Record<typeTeamDiscSize, string> = {
  row: "h-score-mark-size w-score-mark-size text-small",
  header: "h-game-header-mark-size w-game-header-mark-size text-caption",
  page: "h-14 w-14 text-body-strong",
  mini: "h-icon-md w-icon-md text-small",
};

/** The logo's inset on its disc: 2px on the 28 row mark (spec 1.2), 4 on
 *  the header and page marks, 1 on the 20 mini mark. The 2 is the named
 *  `size.scoreMarkLogoInset`, not a spacing step. */
const IMAGE_PADDING_CLASS: Record<typeTeamDiscSize, string> = {
  row: "p-score-mark-logo-inset",
  header: "p-1",
  page: "p-1",
  mini: "p-px",
};

/** Each disc's diameter and its plate letters' font size, from the same
 *  tokens as `SIZE_CLASS` (the page mark is `h-14`, 56). */
const DISC_METRICS: Record<
  typeTeamDiscSize,
  { diameterPx: number; fontPx: number }
> = {
  row: {
    diameterPx: tokens.size.scoreMarkSize,
    fontPx: tokens.text.small.size,
  },
  header: {
    diameterPx: tokens.size.gameHeaderMarkSize,
    fontPx: tokens.text.caption.size,
  },
  page: { diameterPx: 56, fontPx: tokens.text.bodyStrong.size },
  mini: { diameterPx: tokens.size.iconMd, fontPx: tokens.text.small.size },
};

/** Inter's cap height in em. Plate initials are capitals, so the circle's
 *  chord at half this height above and below center is the room they get. */
const CAP_HEIGHT_EM = 0.727;

/**
 * How far to scale a plate's letters so they clear the round edge: the
 * circle's width at the letters' cap height, less 2px each side (the row
 * mark's logo inset, `size.scoreMarkLogoInset`), over
 * the letters' laid out width. 1 when they fit, or before they are measured.
 */
export function plateLettersScale(input: {
  diameterPx: number;
  fontPx: number;
  textWidthPx: number;
}): number {
  if (!(input.textWidthPx > 0)) return 1;
  const radius = input.diameterPx / 2;
  const halfCap = (input.fontPx * CAP_HEIGHT_EM) / 2;
  const chord = 2 * Math.sqrt(Math.max(0, radius * radius - halfCap * halfCap));
  const room = chord - 2 * tokens.size.scoreMarkLogoInset;
  return Math.min(1, room / input.textWidthPx);
}

/** Text on a team color plate: whichever of white and the darkest neutral
 *  reads better on it. Both are theme palette steps, never a literal. */
export function plateInkFor(fillHex: string): string {
  const light = palette.neutral[0];
  const dark = palette.neutral[975];
  try {
    return contrastRatio(fillHex, light) >= contrastRatio(fillHex, dark)
      ? light
      : dark;
  } catch {
    return light;
  }
}

export type typeTeamDiscProps = {
  /** The letters on the plate; at most three are drawn. */
  plateText: string;
  logoUrl?: string | null | undefined;
  plateColorHex?: string | null | undefined;
  size: typeTeamDiscSize;
  testId?: string | undefined;
  imageTestId?: string | undefined;
};

export const TeamDisc = (props: typeTeamDiscProps): ReactNode => {
  const [hasFailed, setHasFailed] = useState(false);
  useEffect(() => {
    setHasFailed(false);
  }, [props.logoUrl]);
  const imageUrl =
    typeof props.logoUrl === "string" && props.logoUrl !== "" && !hasFailed
      ? props.logoUrl
      : null;
  const plateColorHex = props.plateColorHex ?? null;
  const plateStyle: CSSProperties | undefined =
    imageUrl === null && plateColorHex !== null && plateColorHex !== ""
      ? { backgroundColor: plateColorHex, color: plateInkFor(plateColorHex) }
      : undefined;
  // Up to three letters (spec 1.2). The 48px header disc holds four, which is
  // what keeps two teams that plate alike apart there ("WASS" and "WAS",
  // ISSUE-902); the 20px mini disc holds two.
  const letters = props.plateText.slice(
    0,
    props.size === "header" || props.size === "page"
      ? 4
      : props.size === "mini"
        ? 2
        : 3,
  );
  const lettersRef = useRef<HTMLSpanElement | null>(null);
  const [lettersScale, setLettersScale] = useState(1);
  const isPlate = imageUrl === null;
  useLayoutEffect(() => {
    const element = lettersRef.current;
    if (!isPlate || element === null) return;
    let isCurrent = true;
    const { diameterPx, fontPx } = DISC_METRICS[props.size];
    // offsetWidth is the laid out width, untouched by the scale it feeds.
    const measure = (): void => {
      if (!isCurrent) return;
      setLettersScale(
        plateLettersScale({
          diameterPx,
          fontPx,
          textWidthPx: element.offsetWidth,
        }),
      );
    };
    measure();
    // Inter is self-hosted and may land after the first layout; its widths
    // differ from the fallback face's, so measure again once it has.
    void document.fonts?.ready.then(measure);
    return () => {
      isCurrent = false;
    };
  }, [isPlate, letters, props.size]);
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-full font-bold",
        SIZE_CLASS[props.size],
        imageUrl !== null
          ? "border border-hairline bg-mark-logo-surface"
          : plateStyle === undefined
            ? "bg-primary-solid text-on-primary"
            : undefined,
      )}
      style={plateStyle}
      aria-hidden="true"
      data-testid={props.testId ?? "team-disc"}
      data-size={props.size}
    >
      {imageUrl !== null ? (
        <img
          src={imageUrl}
          alt=""
          data-testid={props.imageTestId ?? "team-disc-image"}
          className={cn(
            "h-full w-full object-contain",
            IMAGE_PADDING_CLASS[props.size],
          )}
          onError={() => {
            setHasFailed(true);
          }}
        />
      ) : (
        <span
          ref={lettersRef}
          className="inline-block whitespace-nowrap"
          data-testid="team-disc-letters"
          style={
            lettersScale < 1
              ? { transform: `scale(${String(lettersScale)})` }
              : undefined
          }
        >
          {letters}
        </span>
      )}
    </span>
  );
};
